import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useMemo, useRef, type ReactNode } from 'react';
import { Button, Field, Input, Select, Textarea, type SelectOption } from '@bb/ui';
import type { AdminApi, WritePolicy } from '../../../../api/client.ts';
import { PersonalizationButton } from '../../../composer/shared/PersonalizationButton.tsx';
import {
  actionOfKind,
  cleanAction,
  emptySelector,
  KIND_LABELS,
  KIND_OPTIONS,
  mergeIssues,
  readUserUpdate,
  registryIssues,
  serverFieldIssues,
  USER_UPDATE_MAX_ACTIONS,
  USER_UPDATE_MAX_EXPRESSION,
  userUpdateIssues,
  VALUE_HINTS,
  WITHDRAW_BY_OPTIONS,
  withdrawBy,
  writableCatalog,
  type ActionIssues,
  type UserUpdateForm,
  type UserUpdateFormAction,
  type UserUpdateKind,
  type WithdrawBy,
  type WritableCatalog,
} from './userUpdateConfig.ts';
import './stepPanels.css';

export interface UserUpdatePanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit; the step keeps its one port (`next`). */
  onChange: (next: UserUpdateForm) => void;
  readOnly?: boolean;
  /** `invalid_step_config.fields` of the draft validation for this step (`actions.N.key`, …). */
  serverFields?: ReadonlyArray<{ path: string; message: string }>;
  /** `user_update_reenters` warnings of the draft validation for this step. */
  reentryWarnings?: string[];
}

const useApi = () => (useRouter().options.context as { api: AdminApi }).api;

/** Every page of a cursor list (the registry is small; the cap only guards a runaway cursor). */
async function allPages<T>(page: (cursor: string | undefined) => Promise<{ items: T[]; nextCursor: string | null }>): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let n = 0; n < 50; n++) {
    const result = await page(cursor);
    items.push(...result.items);
    if (!result.nextCursor) break;
    cursor = result.nextCursor;
  }
  return items;
}

/** The attribute and event registries split into what a Canvas may write; `undefined` until both lists are in. */
function useWritableCatalog(environmentId: string): { catalog: WritableCatalog | undefined; failed: boolean; policiesFailed: boolean } {
  const api = useApi();
  const attributes = useQuery({ queryKey: ['attributes', environmentId, 'canvas-writable'], queryFn: () => allPages((cursor) => api.listAttributes(environmentId, cursor ? { cursor } : {})) });
  const events = useQuery({ queryKey: ['events', environmentId, 'canvas-writable'], queryFn: () => allPages((cursor) => api.listEvents(environmentId, cursor ? { cursor } : {})) });
  // The event list has no write policy: the trusted data report names every non-`any` one.
  const trust = useQuery({ queryKey: ['catalog-trust', environmentId, 'policies'], queryFn: () => api.trustReport(environmentId, 1) });
  const catalog = useMemo(() => {
    if (!attributes.data || !events.data || trust.isPending) return undefined;
    const policies = new Map<string, WritePolicy>((trust.data?.items ?? []).map((i) => [`${i.kind}:${i.name}`, i.writePolicy]));
    return writableCatalog(attributes.data, events.data, policies);
  }, [attributes.data, events.data, trust.isPending, trust.data]);
  return { catalog, failed: attributes.isError || events.isError, policiesFailed: trust.isError };
}

/** The choices of a picker; a stored value the list does not offer stays visible, marked. */
function withCurrent(options: SelectOption[], current: string, loaded: boolean, unknownLabel: (v: string) => string): SelectOption[] {
  if (!current || options.some((o) => o.value === current)) return options;
  return [{ value: current, label: loaded ? unknownLabel(current) : current }, ...options];
}

function AttributeField({ action, catalog, issue, readOnly, onKey }: { action: Extract<UserUpdateFormAction, { key: string }>; catalog: WritableCatalog | undefined; issue: string | undefined; readOnly: boolean; onKey: (key: string) => void }) {
  const numbersOnly = action.kind === 'increment_attribute';
  const options = [...(catalog?.attributes.values() ?? [])]
    .filter((a) => !numbersOnly || a.dataType === 'number')
    .map((a) => ({ value: a.key, label: `${a.key} · ${a.dataType}${a.status === 'deprecated' ? ' (deprecated)' : ''}` }));
  return (
    <Field label="Attribute" error={issue} hint={numbersOnly ? 'Number attributes a Canvas may write.' : 'Custom attributes a Canvas may write.'}>
      <Select
        options={withCurrent(options, action.key, Boolean(catalog), (k) => (catalog?.attributes.has(k) ? `${k} · ${catalog.attributes.get(k)!.dataType}` : `${k} (not writable)`))}
        placeholder={catalog ? 'Choose an attribute' : 'Loading attributes…'}
        value={action.key}
        disabled={readOnly}
        onChange={(e) => onKey(e.target.value)}
      />
    </Field>
  );
}

/** A Liquid text box with the personalization button beside it. */
function LiquidField({ environmentId, label, hint, error, value, placeholder, multiline, readOnly, personalizeLabel, onChange }: {
  environmentId: string;
  label: string;
  hint?: ReactNode;
  error: string | undefined;
  value: string;
  placeholder: string;
  multiline: boolean;
  readOnly: boolean;
  personalizeLabel: string;
  onChange: (next: string) => void;
}) {
  const area = useRef<HTMLTextAreaElement>(null);
  const line = useRef<HTMLInputElement>(null);
  return (
    <div className="user-update__liquid">
      <Field label={label} error={error} hint={hint}>
        {multiline ? (
          <Textarea ref={area} rows={2} value={value} spellCheck={false} placeholder={placeholder} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <Input ref={line} value={value} spellCheck={false} autoComplete="off" placeholder={placeholder} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
        )}
      </Field>
      <PersonalizationButton environmentId={environmentId} target={multiline ? area : line} value={value} onChange={onChange} label={personalizeLabel} disabled={readOnly} />
    </div>
  );
}

function MessageSelect({ environmentId, value, error, readOnly, onChange }: { environmentId: string; value: string; error: string | undefined; readOnly: boolean; onChange: (id: string) => void }) {
  const api = useApi();
  // Same list (and cache) as the campaign picker of message-interaction triggers.
  const campaigns = useQuery({ queryKey: ['campaigns', environmentId, 'picker'], queryFn: () => api.listCampaigns(environmentId, { status: 'all', sort: 'name', limit: 200 }) });
  const options = (campaigns.data?.items ?? []).filter((c) => c.status !== 'archived' || c.apiId === value).map((c) => ({ value: c.apiId, label: c.name }));
  return (
    <Field label="Message" error={error} hint="Every card of this campaign.">
      <Select options={withCurrent(options, value, campaigns.isSuccess, (id) => `Unknown message ${id}`)} placeholder={campaigns.isPending ? 'Loading campaigns…' : 'Choose a campaign'} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

function TagSelect({ environmentId, value, error, readOnly, onChange }: { environmentId: string; value: string; error: string | undefined; readOnly: boolean; onChange: (id: string) => void }) {
  const api = useApi();
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const options = (tags.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  return (
    <Field label="Tag" error={error} hint="Cards of every campaign and Canvas with this tag.">
      <Select options={withCurrent(options, value, tags.isSuccess, (id) => `Unknown tag ${id}`)} placeholder={tags.isPending ? 'Loading tags…' : 'Choose a tag'} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

function WithdrawFields({ environmentId, index, action, issues, readOnly, set }: { environmentId: string; index: number; action: Extract<UserUpdateFormAction, { kind: 'withdraw_cards' }>; issues: ActionIssues; readOnly: boolean; set: (next: UserUpdateFormAction) => void }) {
  const by = withdrawBy(action.selector);
  const s = action.selector;
  return (
    <>
      <Field label="Withdraw cards by">
        <Select options={WITHDRAW_BY_OPTIONS} value={by} disabled={readOnly} onChange={(e) => set({ ...action, selector: emptySelector(e.target.value as WithdrawBy) })} />
      </Field>
      {'messageApiId' in s && <MessageSelect environmentId={environmentId} value={s.messageApiId} error={issues.selector} readOnly={readOnly} onChange={(messageApiId) => set({ ...action, selector: { messageApiId } })} />}
      {'tagId' in s && <TagSelect environmentId={environmentId} value={s.tagId} error={issues.selector} readOnly={readOnly} onChange={(tagId) => set({ ...action, selector: { tagId } })} />}
      {'extrasKey' in s && (
        <>
          <Field label="Extras key" error={issues.extrasKey ?? issues.selector}>
            <Input value={s.extrasKey} spellCheck={false} autoComplete="off" placeholder="bonus-campaign" disabled={readOnly} onChange={(e) => set({ ...action, selector: { ...s, extrasKey: e.target.value } })} />
          </Field>
          <LiquidField
            environmentId={environmentId}
            label="Extras value (Liquid)"
            hint="Cards whose extras have this value; Liquid is rendered for each user. An empty value withdraws nothing."
            error={issues.extrasValue}
            value={s.extrasValue}
            placeholder="{{event_properties.${campaign}}}"
            multiline={false}
            readOnly={readOnly}
            personalizeLabel={`Personalize the extras value of action ${index + 1}`}
            onChange={(extrasValue) => set({ ...action, selector: { ...s, extrasValue } })}
          />
        </>
      )}
      <Field label="Reason (optional)" error={issues.reason} hint="Kept on the withdrawn cards; canvas_user_update when empty.">
        <Input value={action.reason ?? ''} autoComplete="off" disabled={readOnly} onChange={(e) => set({ ...action, reason: e.target.value })} />
      </Field>
    </>
  );
}

interface ActionRowProps {
  environmentId: string;
  index: number;
  count: number;
  action: UserUpdateFormAction;
  issues: ActionIssues;
  catalog: WritableCatalog | undefined;
  readOnly: boolean;
  set: (next: UserUpdateFormAction) => void;
  remove: () => void;
}

function ActionRow({ environmentId, index, count, action, issues, catalog, readOnly, set, remove }: ActionRowProps) {
  const n = index + 1;
  const fields = (() => {
    switch (action.kind) {
      case 'set_attribute': {
        const type = catalog?.attributes.get(action.key)?.dataType;
        return (
          <>
            <AttributeField action={action} catalog={catalog} issue={issues.key} readOnly={readOnly} onKey={(key) => set({ ...action, key })} />
            <LiquidField
              environmentId={environmentId}
              label="Value (Liquid or literal)"
              hint={`${type ? `${VALUE_HINTS[type] ?? ''} ` : ''}Liquid is rendered for each user; a literal is checked against the attribute type when the Canvas is validated.`}
              error={issues.valueExpr}
              value={action.valueExpr}
              placeholder="{{event_properties.${tier}}}"
              multiline
              readOnly={readOnly}
              personalizeLabel={`Personalize the value of action ${n}`}
              onChange={(valueExpr) => set({ ...action, valueExpr })}
            />
          </>
        );
      }
      case 'increment_attribute':
        return (
          <>
            <AttributeField action={action} catalog={catalog} issue={issues.key} readOnly={readOnly} onKey={(key) => set({ ...action, key })} />
            <Field label="By" error={issues.by} hint="A negative number decreases the value.">
              <Input type="number" step="any" value={String(action.by)} disabled={readOnly} onChange={(e) => set({ ...action, by: e.target.value === '' ? '' : Number(e.target.value) })} />
            </Field>
          </>
        );
      case 'remove_attribute':
        return <AttributeField action={action} catalog={catalog} issue={issues.key} readOnly={readOnly} onKey={(key) => set({ ...action, key })} />;
      case 'log_event': {
        const options = [...(catalog?.events.values() ?? [])].map((e) => ({ value: e.name, label: e.status === 'deprecated' ? `${e.name} (deprecated)` : e.name }));
        return (
          <>
            <Field label="Event" error={issues.name} hint="Custom events a Canvas may log; it reaches the profile history and triggers like an ingested event.">
              <Select
                options={withCurrent(options, action.name, Boolean(catalog), (name) => `${name} (not writable)`)}
                placeholder={catalog ? 'Choose an event' : 'Loading events…'}
                value={action.name}
                disabled={readOnly}
                onChange={(e) => set({ ...action, name: e.target.value })}
              />
            </Field>
            <LiquidField
              environmentId={environmentId}
              label="Properties (JSON, Liquid allowed)"
              hint="Renders to a JSON object; leave empty for an event without properties."
              error={issues.propertiesExpr}
              value={action.propertiesExpr ?? ''}
              placeholder={'{"campaign": "{{event_properties.${campaign}}}"}'}
              multiline
              readOnly={readOnly}
              personalizeLabel={`Personalize the properties of action ${n}`}
              onChange={(propertiesExpr) => set({ ...action, propertiesExpr })}
            />
          </>
        );
      }
      case 'withdraw_cards':
        return <WithdrawFields environmentId={environmentId} index={index} action={action} issues={issues} readOnly={readOnly} set={set} />;
    }
  })();
  return (
    <li>
      <section aria-label={`Action ${n}: ${KIND_LABELS[action.kind]}`} className="step-panel__group user-update__action">
        <div className="user-update__head">
          <Field label="Action" error={issues.kind}>
            <Select options={KIND_OPTIONS} value={action.kind} disabled={readOnly} onChange={(e) => set(actionOfKind(e.target.value as UserUpdateKind, action))} />
          </Field>
          {!readOnly && (
            <Button size="sm" variant="ghost" aria-label={`Remove action ${n}`} disabled={count === 1} onClick={remove}>
              Remove
            </Button>
          )}
        </div>
        {fields}
      </section>
    </li>
  );
}

/**
 * Settings of a User Update step (BB-120, CAN-010): 1–10 typed actions instead of Braze's free JSON — set, increment or
 * remove a custom attribute a Canvas may write, log a custom event, withdraw cards (BB-65). The pickers offer only what
 * a Canvas may write (no trusted, PII or blocked attributes; no blocked or test-only events); values and event
 * properties may be Liquid, rendered for each user. Field problems come from the contract, the registry and the
 * server's draft validation.
 */
export function UserUpdatePanel({ environmentId, value, onChange, readOnly = false, serverFields = [], reentryWarnings = [] }: UserUpdatePanelProps) {
  const config = readUserUpdate(value);
  const { catalog, failed, policiesFailed } = useWritableCatalog(environmentId);
  const issues = mergeIssues(userUpdateIssues(config), catalog ? registryIssues(config, catalog) : { each: [] }, serverFieldIssues(serverFields));
  const save = (actions: UserUpdateFormAction[]) => onChange({ actions: actions.map(cleanAction) });

  return (
    <section className="step-panel" aria-label="User Update settings">
      <p className="hint">
        The actions run once, when a user reaches this step, and the user goes on. Only attributes and events a Canvas may write are offered: trusted (backend-only), PII and blocked attributes, and blocked or test-only events are
        not. One attribute takes at most one action.
      </p>
      {failed && <p className="notice notice--warning">The attribute or event registry could not be loaded; the server checks them when the Canvas is validated.</p>}
      {!failed && policiesFailed && <p className="notice notice--warning">Write policies could not be loaded: trusted events may still be listed; the server refuses them when the Canvas is validated.</p>}
      {reentryWarnings.length > 0 && (
        <section role="note" aria-label="Re-entry" className="notice notice--warning">
          {reentryWarnings.map((w) => (
            <p key={w}>{w.charAt(0).toUpperCase() + w.slice(1)}.</p>
          ))}
          <p className="hint">Check the re-entry settings of the entry: they are the only thing that stops the loop.</p>
        </section>
      )}
      <ol className="user-update__actions" aria-label="Actions">
        {config.actions.map((action, index) => (
          <ActionRow
            // Positions: changing the kind replaces the action, and there is no id to key by.
            key={index}
            environmentId={environmentId}
            index={index}
            count={config.actions.length}
            action={action}
            issues={issues.each[index] ?? {}}
            catalog={catalog}
            readOnly={readOnly}
            set={(next) => save(config.actions.map((a, i) => (i === index ? next : a)))}
            remove={() => save(config.actions.filter((_, i) => i !== index))}
          />
        ))}
      </ol>
      {issues.actions && (
        <p className="bb-field__error" role="alert">
          {issues.actions}
        </p>
      )}
      {!readOnly && (
        <div className="filters">
          <Button size="sm" disabled={config.actions.length >= USER_UPDATE_MAX_ACTIONS} onClick={() => save([...config.actions, actionOfKind('set_attribute')])}>
            Add action
          </Button>
          <span className="hint">
            {config.actions.length} of {USER_UPDATE_MAX_ACTIONS} actions
          </span>
        </div>
      )}
      <p className="hint">
        Each action stands alone: one that cannot be applied — a Liquid error, abort_message, the wrong type, an attribute or event a Canvas may no longer write — is not updated, the others still are and the user goes on. Values
        and properties have at most {USER_UPDATE_MAX_EXPRESSION.toLocaleString('en-US')} characters.
      </p>
    </section>
  );
}
